import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
// NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1
import { agentCableTokenProvider } from '../../shared/helpers/KaptionCableToken';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
// NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.1
import { notePresenceAck } from 'dashboard/composables/useKaptionLivePresence';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    // NOTICE: KAPTION-INTEGRATION - realtime-auth.SEC.1: subscribe with a short-lived cable token
    super(
      app,
      pubsubToken,
      websocketURL,
      agentCableTokenProvider(window.axios)
    );
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'copilot.message.created': this.onCopilotMessageCreated,
      // NOTICE: KAPTION-INTEGRATION - team-location.LIVE.1
      'kaption.location.updated': this.onKaptionLocationUpdated,
      // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.EVENTS.1..4
      'kaption_call.ringing': this.onKaptionCallEvent('kaption_call.ringing'),
      'kaption_call.answered': this.onKaptionCallEvent('kaption_call.answered'),
      'kaption_call.updated': this.onKaptionCallEvent('kaption_call.updated'),
      'kaption_call.ended': this.onKaptionCallEvent('kaption_call.ended'),
      'kaption_call.availability_changed': this.onKaptionCallEvent(
        'kaption_call.availability_changed'
      ),
      // NOTICE: KAPTION-INTEGRATION - whatsapp-calling-ownership.PASS.9, LINE.3
      'kaption_call.pass_ringing': this.onKaptionCallEvent(
        'kaption_call.pass_ringing'
      ),
      'kaption_call.pass_failed': this.onKaptionCallEvent(
        'kaption_call.pass_failed'
      ),
      'kaption_call.passed': this.onKaptionCallEvent('kaption_call.passed'),
      'kaption_call.line_changed': this.onKaptionCallEvent(
        'kaption_call.line_changed'
      ),
      // NOTICE: KAPTION-INTEGRATION - whatsapp-call-transcripts.EVENTS.2
      'kaption_call.transcript_updated': this.onKaptionCallEvent(
        'kaption_call.transcript_updated'
      ),
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  // NOTICE: KAPTION-INTEGRATION - team-location.LIVE.1: re-emit live location
  // points on the bus; the team location map listens while it is open.
  onKaptionLocationUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit('kaption.location.updated', data);
  };

  // NOTICE: KAPTION-INTEGRATION - whatsapp-calling.EVENTS.1..4: re-emit call
  // events on the bus for the call store (components-next/kaption/calls). User-
  // stream payloads may omit account_id; only a mismatching one is dropped.
  onKaptionCallEvent = eventName => data => {
    if (
      data?.account_id &&
      data.account_id !== this.app.$store.getters.getCurrentAccountId
    )
      return;
    emitter.emit(eventName, data);
  };

  // NOTICE: KAPTION-INTEGRATION - Add validation for message updates
  onMessageUpdated = data => {
    // Only process messages for the current account
    if (!this.isAValidEvent(data)) {
      return;
    }

    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    // NOTICE: KAPTION-INTEGRATION — agent-presence.VISIBILITY.1
    // RoomChannel answers every heartbeat with this broadcast on the user's own
    // pubsub token, so receiving it is proof that a beat landed. It is the only
    // signal that catches a heartbeat which stopped while the socket stayed
    // open — a frozen or throttled background tab.
    notePresenceAck();
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
